'use client';

import React from 'react';
import Link from 'next/link';
import { useActiveCategories } from '@/hooks/useCategories';
import { getLucideIcon } from '@/utils/getLucideIcon';
import { SectionWrapper, SectionHeading } from '../../components/common';

/**
 * Browse-by-category — same data path as main marketing home (`useActiveCategories` →
 * `get_active_categories_public` RPC) with v2 styling and `/browse?categories=` links.
 */
export const CategoryGrid: React.FC = () => {
  const { data: activeCategories = [], isLoading, isError, error } = useActiveCategories();

  return (
    <SectionWrapper bg="white" py="lg">
      <SectionHeading
        title="Browse by Category"
        description="Find exactly what you need from a variety of rental categories"
      />

      {isError && (
        <p className="mb-4 text-center text-sm text-v2-red-500">
          {error?.message ?? 'Could not load categories.'}
        </p>
      )}

      {isLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 sm:gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="min-h-[100px] animate-pulse rounded-v2-md bg-v2-neutral-100 sm:min-h-[112px]"
            />
          ))}
        </div>
      ) : activeCategories.length === 0 ? (
        <p className="py-8 text-center text-sm text-v2-neutral-500">No categories available right now.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 sm:gap-4">
          {activeCategories.map((category) => {
            const Icon = getLucideIcon(category.icon);
            const slug = category.name;
            return (
              <Link
                key={category.id}
                href={`/browse?categories=${slug}`}
                className="group flex min-h-[100px] flex-col items-center justify-center gap-2 rounded-v2-md border border-v2-neutral-100 bg-v2-neutral-50 px-2 py-4 text-center transition-colors duration-150 hover:bg-v2-neutral-100 sm:min-h-[112px] sm:py-5"
              >
                <Icon
                  strokeWidth={1.25}
                  className="h-10 w-10 shrink-0 text-v2-green-500 transition-transform group-hover:scale-110 sm:h-11 sm:w-11"
                />
                <span className="text-[12px] font-semibold leading-tight text-v2-neutral-700 sm:text-[13px]">
                  {category.title}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </SectionWrapper>
  );
};
